/** @jsx jsx */
import { jsx } from '@emotion/core'
import { Radio } from 'antd'
import { MarketBlock, MarketTrack, MatchTrack, OutcomeTrack, TournamentTrack } from '@store/matchupStore'
import { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { isMobile } from 'react-device-detect'
import _ from 'lodash'
import { Accordion } from 'antd-mobile'

import { ENABLE_MIN_BET_RATE, makeSelectionValue } from '@game/util'
import { LS_OUTCOME_TYPE, OUTCOME_TYPE } from '@constants/market'
import { CircleDownIco, DownIco, UnderOverArrowIco, UpIco } from '@shared/Icon/ArrowIcon'
import { ChangeNum, Hit, OddsChangeAnimationStopTime, UnderOverIco, UpDownIcoStyle } from '@styles/match.style'
import { Schema$MarketBlock, Schema$MarketTrack, Schema$MatchTrack } from '@protocol/game'
import { PAGE_TYPE } from '@interface/common'
import useRootStore from '@store/useRootStore'
import { useOnMount } from '@utils/reactExt'
import { EventStatusType, MarketStatusType, ProducerType } from '@game-type/betradar-type'
import { convertRate, isLsportsEnv } from '@utils/utils'
import { LS_MATCH_STATUS } from 'adv-protocol/src/GameCommon'

import { BLOCK_TYPE } from '../../OddsBlock'
import OddsBlockModal from '../OddsBlockModal'
import DialogUtils from '@utils/dialog-utils'

interface OddsButtonGroupProps {
  pageType?: PAGE_TYPE
  oddsBlockType: BLOCK_TYPE
  match: MatchTrack
  odds?: MarketTrack
  track: TournamentTrack
  tournamentId: number
  onFieldClickCheck: () => boolean
  isTwoWay: boolean
  minimumLiveMatchOutcomeRate: number
}

export const Up = (
  <UnderOverIco className="underover-ico up">
    <UnderOverArrowIco/>
  </UnderOverIco>
)
export const Down = (
  <UnderOverIco className="underover-ico down">
    <UnderOverArrowIco/>
  </UnderOverIco>
)
export const PointUp = (
  <UpDownIcoStyle className="up">
    <UpIco/>
  </UpDownIcoStyle>
)
export const PointDown = (
  <UpDownIcoStyle className="down">
    <DownIco/>
  </UpDownIcoStyle>
)

// 핸디캡이나 언더오버에서 기준점 0.5 로 되어있는 것만 필터링하는 함수
function makeMarkets(oddsBlockType: string, odds: Schema$MarketTrack) {
  if (oddsBlockType === BLOCK_TYPE.HANDICAP || oddsBlockType === BLOCK_TYPE.UNDEROVER) {
    const initMarkets: Schema$MarketBlock[] = []

    for (const el of odds.markets ?? []) {
      const hcp = Number(_.replace(el.specifiers.hcp, ' (0-0)', ''))
      const intNumber = parseInt(_.replace(el.specifiers.hcp, ' (0-0)', ''), 10)
      if (hcp === 0 || Math.abs(hcp - intNumber) === 0.5) {
        initMarkets.push(el)
      }
    }

    if (initMarkets.length > 0) {
      return initMarkets
    }
    return odds.markets
  }
  return odds ? odds.markets : null
}

function getRateAndResultInfo(
  match: Schema$MatchTrack,
  markets: Schema$MarketBlock[],
  oddsBlockType: string,
  homeKey: string,
  drawKey: string,
  awayKey: string,
) {
  let homeRate = 0
  let drawRate = 0
  let awayRate = 0
  let homeActive = true
  let drawActive = true
  let awayActive = true
  let homeOldRate = 0
  let drawOldRate = 0
  let awayOldRate = 0
  let isHomeHit = false
  let isDrawHit = false
  let isAwayHit = false
  let homeOutcomeMetaId = null
  let drawOutcomeMetaId = null
  let awayOutcomeMetaId = null
  let lsHomeOutcomeId = null
  let lsDrawOutcomeId = null
  let lsAwayOutcomeId = null
  const { matchStatusCode } = match
  if (markets) {
    if (oddsBlockType === BLOCK_TYPE.WDL || oddsBlockType === BLOCK_TYPE.LIVE) {
      if (oddsBlockType !== BLOCK_TYPE.LIVE
        || (!isLsportsEnv() && matchStatusCode !== EventStatusType.NotStarted)
        || (isLsportsEnv() && (matchStatusCode !== LS_MATCH_STATUS.NOT_STARTED
          && matchStatusCode !== LS_MATCH_STATUS.NOT_START_YET))
      ) {
        let oddsHome: OutcomeTrack
        let oddsDraw: OutcomeTrack
        let oddsAway: OutcomeTrack

        const emptyOdds: OutcomeTrack = {
          type: null, // 벳레이더는 메타아이디, Lsports 는 벳아이디(유니크)
          rate: 0,
          oldRate: 0,
          isActive: true,
          result: 0,
          metaId: null, // 벳레이더는 null, Lsports 만 메타아이디
        }
        if (isLsportsEnv()) {
          oddsHome = _.find<OutcomeTrack>(markets[0].outcomes, { metaId: homeKey }) ?? emptyOdds
          oddsDraw = _.find<OutcomeTrack>(markets[0].outcomes, { metaId: drawKey }) ?? emptyOdds
          oddsAway = _.find<OutcomeTrack>(markets[0].outcomes, { metaId: awayKey }) ?? emptyOdds
        } else {
          oddsHome = _.find<OutcomeTrack>(markets[0].outcomes, { type: homeKey }) ?? emptyOdds
          oddsDraw = _.find<OutcomeTrack>(markets[0].outcomes, { type: drawKey }) ?? emptyOdds
          oddsAway = _.find<OutcomeTrack>(markets[0].outcomes, { type: awayKey }) ?? emptyOdds
        }

        homeRate = oddsHome.rate ?? 0
        drawRate = oddsDraw.rate ?? 0
        awayRate = oddsAway.rate ?? 0

        homeOldRate = oddsHome.oldRate ?? 0
        drawOldRate = oddsDraw.oldRate ?? 0
        awayOldRate = oddsAway.oldRate ?? 0

        homeActive = oddsHome.isActive
        drawActive = oddsDraw.isActive
        awayActive = oddsAway.isActive

        isHomeHit = oddsHome.result === 1
        isDrawHit = oddsDraw.result === 1
        isAwayHit = oddsAway.result === 1

        homeOutcomeMetaId = oddsHome.metaId
        drawOutcomeMetaId = oddsDraw.metaId
        awayOutcomeMetaId = oddsAway.metaId
        lsHomeOutcomeId = oddsHome.type
        lsDrawOutcomeId = oddsDraw.type
        lsAwayOutcomeId = oddsAway.type
      }
    } else {
      const representativeMarket = getRepresentativeMarket(markets)
      const emptyOdds: OutcomeTrack = {
        type: null,
        rate: 0,
        oldRate: 0,
        isActive: true,
        result: 0,
        metaId: null,
      }

      let oddsHome: OutcomeTrack
      let oddsAway: OutcomeTrack
      if (isLsportsEnv()) {
        oddsHome = _.find<OutcomeTrack>(representativeMarket.outcomes, { metaId: homeKey }) ?? emptyOdds
        oddsAway = _.find<OutcomeTrack>(representativeMarket.outcomes, { metaId: awayKey }) ?? emptyOdds
      } else {
        oddsHome = _.find<OutcomeTrack>(representativeMarket.outcomes, { type: homeKey }) ?? emptyOdds
        oddsAway = _.find<OutcomeTrack>(representativeMarket.outcomes, { type: awayKey }) ?? emptyOdds
      }

      homeRate = oddsHome.rate ?? 0
      awayRate = oddsAway.rate ?? 0

      homeOldRate = oddsHome.oldRate ?? 0
      awayOldRate = oddsAway.oldRate ?? 0

      homeActive = oddsHome.isActive
      awayActive = oddsAway.isActive

      isHomeHit = oddsHome.result === 1
      isAwayHit = oddsAway.result === 1

      homeOutcomeMetaId = oddsHome.metaId
      awayOutcomeMetaId = oddsAway.metaId

      lsHomeOutcomeId = oddsHome.type
      lsAwayOutcomeId = oddsAway.type
    }
  }

  return {
    homeRate,
    drawRate,
    awayRate,
    homeOldRate,
    drawOldRate,
    awayOldRate,
    homeActive,
    drawActive,
    awayActive,
    isHomeHit,
    isDrawHit,
    isAwayHit,
    homeOutcomeMetaId,
    drawOutcomeMetaId,
    awayOutcomeMetaId,
    lsHomeOutcomeId,
    lsDrawOutcomeId,
    lsAwayOutcomeId,
  }
}

export function getSpecialBetValue(el: MarketBlock, oddsBlockType) {
  const { specifiers = {} } = el
  switch (oddsBlockType) {
    case BLOCK_TYPE.WDL:
      return null
    case BLOCK_TYPE.HANDICAP:
      return _.replace(specifiers.hcp, ' (0-0)', '')
    case BLOCK_TYPE.UNDEROVER:
      return specifiers.total
  }

  return null
}

function makeInitialValues(
  market: Schema$MarketTrack,
  match: Schema$MatchTrack,
  markets: Schema$MarketBlock[],
  oddsBlockType: BLOCK_TYPE,
  homeKey: string,
  drawKey: string,
  awayKey: string,
  producerId: ProducerType,
  minimumLiveMatchOutcomeRate: number,
) {
  const marketType = market?.type ?? ''
  const { matchStatusCode } = match

  let homeValue
  let awayValue
  let drawValue
  let specialBetValue
  let disabledHome = true
  let disabledAway = true
  let disabledDraw = true

  const rateInfo = getRateAndResultInfo(
    match,
    markets,
    oddsBlockType,
    homeKey,
    drawKey,
    awayKey,
  )

  const {
    homeRate,
    drawRate,
    awayRate,
    homeOldRate,
    drawOldRate,
    awayOldRate,
    isHomeHit,
    isDrawHit,
    isAwayHit,
    homeOutcomeMetaId,
    drawOutcomeMetaId,
    awayOutcomeMetaId,
    lsHomeOutcomeId,
    lsDrawOutcomeId,
    lsAwayOutcomeId,
  } = rateInfo

  // Lsports 는 type 이 아웃컴 고유 아이디, 벳레이더는 아웃컴 메타 아이디.
  const homeOutcomeType = isLsportsEnv() ? lsHomeOutcomeId : homeKey
  const drawOutcomeType = isLsportsEnv() ? lsDrawOutcomeId : drawKey
  const awayOutcomeType = isLsportsEnv() ? lsAwayOutcomeId : awayKey

  if (markets) {
    if (oddsBlockType === BLOCK_TYPE.WDL || oddsBlockType === BLOCK_TYPE.LIVE) {
      const specifiers = JSON.stringify(markets[0].specifiers)
      homeValue = makeSelectionValue({
        marketType,
        specifiers,
        outcomeType: homeOutcomeType,
        rate: homeRate,
        outcomeMetaId: homeOutcomeMetaId,
        key: homeKey,
        producerId,
      })

      drawValue = makeSelectionValue({
        marketType,
        specifiers,
        outcomeType: drawOutcomeType,
        rate: drawRate,
        outcomeMetaId: drawOutcomeMetaId,
        key: drawKey,
        producerId,
      })

      awayValue = makeSelectionValue({
        marketType,
        specifiers,
        outcomeType: awayOutcomeType,
        rate: awayRate,
        outcomeMetaId: awayOutcomeMetaId,
        key: awayKey,
        producerId,
      })

      if (oddsBlockType === BLOCK_TYPE.LIVE) {
        if (isLsportsEnv()) {
          disabledHome =
            matchStatusCode === LS_MATCH_STATUS.NOT_START_YET ||
            matchStatusCode === LS_MATCH_STATUS.NOT_STARTED ||
            matchStatusCode === LS_MATCH_STATUS.ABOUT_TO_START ||
            rateInfo.homeActive === false ||
            homeRate < ENABLE_MIN_BET_RATE ||
            homeRate < minimumLiveMatchOutcomeRate
          disabledDraw =
            matchStatusCode === LS_MATCH_STATUS.NOT_START_YET ||
            matchStatusCode === LS_MATCH_STATUS.NOT_STARTED ||
            matchStatusCode === LS_MATCH_STATUS.ABOUT_TO_START ||
            rateInfo.drawActive === false ||
            drawRate < ENABLE_MIN_BET_RATE ||
            drawRate < minimumLiveMatchOutcomeRate
          disabledAway =
            matchStatusCode === LS_MATCH_STATUS.NOT_START_YET ||
            matchStatusCode === LS_MATCH_STATUS.NOT_STARTED ||
            matchStatusCode === LS_MATCH_STATUS.ABOUT_TO_START ||
            rateInfo.awayActive === false ||
            awayRate < ENABLE_MIN_BET_RATE ||
            awayRate < minimumLiveMatchOutcomeRate
        } else {
          disabledHome =
            matchStatusCode === EventStatusType.NotStarted ||
            rateInfo.homeActive === false ||
            homeRate < ENABLE_MIN_BET_RATE ||
            homeRate < minimumLiveMatchOutcomeRate
          disabledDraw =
            matchStatusCode === EventStatusType.NotStarted ||
            rateInfo.drawActive === false ||
            drawRate < ENABLE_MIN_BET_RATE ||
            drawRate < minimumLiveMatchOutcomeRate
          disabledAway =
            matchStatusCode === EventStatusType.NotStarted ||
            rateInfo.awayActive === false ||
            awayRate < ENABLE_MIN_BET_RATE ||
            awayRate < minimumLiveMatchOutcomeRate
        }
      } else {
        disabledHome = homeRate < ENABLE_MIN_BET_RATE
        disabledDraw = drawRate < ENABLE_MIN_BET_RATE
        disabledAway = awayRate < ENABLE_MIN_BET_RATE
      }
    } else {
      const representativeMarket = getRepresentativeMarket(markets)

      const specifiers = JSON.stringify(representativeMarket.specifiers)
      specialBetValue = getSpecialBetValue(representativeMarket, oddsBlockType)

      homeValue = makeSelectionValue({
        marketType,
        specifiers,
        outcomeType: homeOutcomeType,
        rate: homeRate,
        outcomeMetaId: homeOutcomeMetaId,
        key: homeKey,
        producerId,
      })

      awayValue = makeSelectionValue({
        marketType,
        specifiers,
        outcomeType: awayOutcomeType,
        rate: awayRate,
        outcomeMetaId: awayOutcomeMetaId,
        key: awayKey,
        producerId,
      })

      disabledHome = homeRate < ENABLE_MIN_BET_RATE
      disabledAway = awayRate < ENABLE_MIN_BET_RATE
    }
  }

  return {
    homeRate,
    drawRate,
    awayRate,
    homeOldRate,
    drawOldRate,
    awayOldRate,
    homeValue,
    awayValue,
    drawValue,
    specialBetValue,
    disabledHome,
    disabledAway,
    disabledDraw,
    isHomeHit,
    isDrawHit,
    isAwayHit,
    homeOutcomeMetaId,
    drawOutcomeMetaId,
    awayOutcomeMetaId,
    homeOutcomeType,
    drawOutcomeType,
    awayOutcomeType,
  }
}

function makeKeyGroup(oddsBlockType: string, isTwoWay: boolean) {
  const keyGroup = { homeKey: '', drawKey: '', awayKey: '' }
  if (isLsportsEnv()) {
    switch (oddsBlockType) {
      case BLOCK_TYPE.WDL:
      case BLOCK_TYPE.LIVE:
        keyGroup.homeKey = LS_OUTCOME_TYPE.WAY_HOME
        keyGroup.drawKey = isTwoWay ? undefined : LS_OUTCOME_TYPE.WAY_DRAW
        keyGroup.awayKey = LS_OUTCOME_TYPE.WAY_AWAY
        break
      case BLOCK_TYPE.HANDICAP:
        keyGroup.homeKey = LS_OUTCOME_TYPE.HANDICAP_HOME
        keyGroup.awayKey = LS_OUTCOME_TYPE.HANDICAP_AWAY
        break
      case BLOCK_TYPE.UNDEROVER:
        keyGroup.homeKey = LS_OUTCOME_TYPE.TOTAL_UNDER
        keyGroup.awayKey = LS_OUTCOME_TYPE.TOTAL_OVER
        break
    }
  } else {
    switch (oddsBlockType) {
      case BLOCK_TYPE.WDL:
      case BLOCK_TYPE.LIVE:
        keyGroup.homeKey = isTwoWay ? OUTCOME_TYPE.TWO_WAY_HOME : OUTCOME_TYPE.WAY_HOME
        keyGroup.drawKey = isTwoWay ? undefined : OUTCOME_TYPE.WAY_DRAW
        keyGroup.awayKey = isTwoWay ? OUTCOME_TYPE.TWO_WAY_AWAY : OUTCOME_TYPE.WAY_AWAY
        break
      case BLOCK_TYPE.HANDICAP:
        keyGroup.homeKey = OUTCOME_TYPE.HANDICAP_HOME
        keyGroup.awayKey = OUTCOME_TYPE.HANDICAP_AWAY
        break
      case BLOCK_TYPE.UNDEROVER:
        keyGroup.homeKey = OUTCOME_TYPE.TOTAL_UNDER
        keyGroup.awayKey = OUTCOME_TYPE.TOTAL_OVER
        break
    }
  }

  return keyGroup
}

function getRepresentativeIndex(length: number): number {
  return Math.floor(length / 2)
}

function getRepresentativeMarket(markets: Schema$MarketBlock[]) {
  const index = getRepresentativeIndex(markets.length)
  return markets[index]
}

function extractOddsBlockModalMarkets(markets: Schema$MarketBlock[]): Schema$MarketBlock[] {
  const excludeMarket = getRepresentativeMarket(markets)
  return _.filter(markets, market => {
    return !(market.type === excludeMarket.type && market.specialBetValue === excludeMarket.specialBetValue)
  })
}

function OddsButtonGroup(props: OddsButtonGroupProps) {
  const { matchupStore, authStore } = useRootStore()
  const {
    pageType,
    oddsBlockType,
    match,
    odds,
    tournamentId,
    onFieldClickCheck,
    isTwoWay,
    minimumLiveMatchOutcomeRate,
  } = props
  const { matchId } = match
  const markets = makeMarkets(oddsBlockType, odds)

  let oddsBlockModalMarkets = []
  if (!(oddsBlockType === BLOCK_TYPE.WDL || oddsBlockType === BLOCK_TYPE.LIVE)) {
    oddsBlockModalMarkets = extractOddsBlockModalMarkets(markets)
  }

  const keyGroup = makeKeyGroup(oddsBlockType, isTwoWay)
  const homeKey = keyGroup.homeKey
  const drawKey = keyGroup.drawKey
  const awayKey = keyGroup.awayKey
  const [field, setField] = useState('')
  const producerId = matchupStore.producerId

  const initialValues = makeInitialValues(
    odds,
    match,
    markets,
    oddsBlockType,
    homeKey,
    drawKey,
    awayKey,
    producerId,
    minimumLiveMatchOutcomeRate,
  )

  const { homeOutcomeType, awayOutcomeType } = initialValues

  const [timerIDHome, setTimerIDHome] = useState(null)
  const [timerIDDraw, setTimerIDDraw] = useState(null)
  const [timerIDAway, setTimerIDAway] = useState(null)
  const [disabled, setDisabled] = useState(false)
  const [update, setUpdate] = useState(0)
  const [disabledHome, setDisabledHome] = useState(initialValues.disabledHome)

  // 동일경기조합 설정에 따라 OddsBlockModal 마켓의 활성 비활성 상태 변경을 위한 state
  const [enabledHomeFromViewController, setEnabledHomeFromViewController] = useState(true)
  const [enabledAwayFromViewController, setEnabledAwayFromViewController] = useState(true)

  const [disabledDraw, setDisabledDraw] = useState(initialValues.disabledDraw)
  const [disabledAway, setDisabledAway] = useState(initialValues.disabledAway)
  const refHomeRate = useRef(initialValues.homeRate)
  const refDrawRate = useRef(initialValues.drawRate)
  const refAwayRate = useRef(initialValues.awayRate)
  const [homeRate, setHomeRate] = useState(refHomeRate.current)
  const [drawRate, setDrawRate] = useState(refDrawRate.current)
  const [awayRate, setAwayRate] = useState(refAwayRate.current)
  const [homeValue, setHomeValue] = useState(initialValues.homeValue)
  const [drawValue, setDrawValue] = useState(initialValues.drawValue)
  const [awayValue, setAwayValue] = useState(initialValues.awayValue)
  const [specialBetValue] = useState(initialValues.specialBetValue)
  const [homeRateChangeClass, setHomeRateChangeClass] = useState('')
  const [drawRateChangeClass, setDrawRateChangeClass] = useState('')
  const [awayRateChangeClass, setAwayRateChangeClass] = useState('')
  const [homeChangedRate, setHomeChangedRate] = useState(0)
  const [drawChangedRate, setDrawChangedRate] = useState(0)
  const [awayChangedRate, setAwayChangedRate] = useState(0)
  const [homeOldRate, setHomeOldRate] = useState(initialValues.homeRate)
  const [drawOldRate, setDrawOldRate] = useState(initialValues.drawRate)
  const [awayOldRate, setAwayOldRate] = useState(initialValues.awayRate)
  const isHomeHit = initialValues.isHomeHit
  const isDrawHit = initialValues.isDrawHit
  const isAwayHit = initialValues.isAwayHit

  const groupKey = `OddsButtonGroup:Group:${match.id}:${odds ? odds.id : ''}`

  const setRefHomeRate = (rate: number): void => {
    refHomeRate.current = rate
    setHomeRate(rate)
  }

  const setRefDrawRate = (rate: number): void => {
    refDrawRate.current = rate
    setDrawRate(rate)
  }

  const setRefAwayRate = (rate: number): void => {
    refAwayRate.current = rate
    setAwayRate(rate)
  }

  const applyFieldValidate = (newField: string): boolean => {
    if (!odds) {
      return false
    }

    if (onFieldClickCheck) {
      const conti = onFieldClickCheck()
      if (conti === false) {
        return false
      }
    }

    if (!authStore.isSignedIn) {
      DialogUtils.showGuestMode()
      return false
    }

    const { producerId } = matchupStore
    const { type: marketType } = odds

    if (producerId !== ProducerType.LIVEODDS && !matchupStore.isPossibleMultiBet(matchId, marketType)) {
      matchupStore.setSelectedCartParams(tournamentId, matchId, newField)
      setField('')

      DialogUtils.showIncompatibleMarketType()

      // 동일 경기 선택 모달 (취소 여부)
      // globalStore.toggleSameMatchPopup(true)
      return false
    }

    return true
  }

  const isDeselectPossibleFiled = (newField: string): boolean => {
    return newField === field
  }

  const saveField = (newField: string): void => {
    const cartItem = matchupStore.addCartItem(tournamentId, matchId, newField)
    if (cartItem) {
      setField(newField)
    }
  }

  const resetField = (newField: string): void => {
    matchupStore.removeCartItem(tournamentId, matchId, newField)
    setField('')
  }

  const applyField = (newField: string): void => {
    if (isDeselectPossibleFiled(newField)) {
      resetField(newField)
      return
    }

    saveField(newField)
  }

  const onClickField = (e): void => {
    const newField = e.target.value
    if (!applyFieldValidate(newField)) {
      return
    }
    applyField(newField)
  }

  const restoreOddsButtonGroupStateForCallFromParent = (
    oddsBlockTypeParam: BLOCK_TYPE,
    value: string,
    marketList: Schema$MarketBlock[],
    marketType: string,
    sportId: number,
  ): void => {
    setField(value)
    matchupStore.updateFightingOdds(matchId, marketType, false)
    matchupStore.updateDisabledOdds(matchId, marketType, sportId, value)
  }

  const clearField = (): void => {
    setField('')
  }

  const setEnabled = (enabled: boolean): void => {
    setDisabled(!enabled)
  }

  const setEnabledHome = (enabled: boolean): void => {
    if (refHomeRate.current < ENABLE_MIN_BET_RATE ||
      (oddsBlockType === BLOCK_TYPE.LIVE && refHomeRate.current < minimumLiveMatchOutcomeRate)) {
      setDisabledHome(true)
    } else {
      setDisabledHome(!enabled)
    }
  }

  const setEnabledDraw = (enabled: boolean): void => {
    if (refDrawRate.current < ENABLE_MIN_BET_RATE ||
      (oddsBlockType === BLOCK_TYPE.LIVE && refDrawRate.current < minimumLiveMatchOutcomeRate)) {
      setDisabledDraw(true)
    } else {
      setDisabledDraw(!enabled)
    }
  }

  const setEnabledAway = (enabled: boolean): void => {
    if (refAwayRate.current < ENABLE_MIN_BET_RATE ||
      (oddsBlockType === BLOCK_TYPE.LIVE && refAwayRate.current < minimumLiveMatchOutcomeRate)) {
      setDisabledAway(true)
    } else {
      setDisabledAway(!enabled)
    }
  }

  const updateField = (): void => {
    setUpdate(update + 1)
  }

  const addViewController = () => {
    if (odds) {
      const { type: marketType } = odds
      matchupStore.addViewController(
        matchId,
        marketType,
        clearField,
        setEnabled,
        updateField,
        setEnabledHome,
        setEnabledDraw,
        setEnabledAway,
        isTwoWay,
        field,
        oddsBlockType,
        restoreOddsButtonGroupStateForCallFromParent,
        setEnabledHomeFromViewController,
        setEnabledAwayFromViewController,
      )
    }
  }

  // NOTE : 라이브의 경우 경기시작전에서 경기 시작으로 상태값이 바뀌면서 실시간으로 배당률을 보여줘야 하는데.
  //         useEffect에서 경기 시작하면서 생긴 배당률을 한번 setState 해준다.
  const liveInitRate = () => {
    if (homeRate === 0 && initialValues.homeRate !== 0) {
      setRefHomeRate(initialValues.homeRate)
      setHomeOldRate(initialValues.homeRate)
      setHomeValue(initialValues.homeValue)
    }

    if (drawRate === 0 && initialValues.drawRate !== 0) {
      setRefDrawRate(initialValues.drawRate)
      setDrawOldRate(initialValues.drawRate)
      setDrawValue(initialValues.drawValue)
    }

    if (awayRate === 0 && initialValues.awayRate !== 0) {
      setRefAwayRate(initialValues.awayRate)
      setAwayOldRate(initialValues.awayRate)
      setAwayValue(initialValues.awayValue)
    }
  }

  const checkOddsStatusChange = () => {
    if (oddsBlockType === BLOCK_TYPE.LIVE) {
      // NOTE : 라이브 경기의 경우 실시간으로 이전 enable 상태값과 비교 해서 다를 경우 최신 상태로 set해준다.
      if (markets && markets[0].status !== MarketStatusType.Active) {
        setDisabled(true)
      } else if (markets && markets[0].status === MarketStatusType.Active && disabled) {
        setDisabled(false)
      }

      if (initialValues.disabledHome !== disabledHome) {
        setDisabledHome(initialValues.disabledHome)
      }

      if (initialValues.disabledDraw !== disabledDraw) {
        setDisabledDraw(initialValues.disabledDraw)
      }

      if (initialValues.disabledAway !== disabledAway) {
        setDisabledAway(initialValues.disabledAway)
      }

      liveInitRate()
    }

    const homeChangedRateValue = Number((homeOldRate !== 0 ? initialValues.homeRate - homeOldRate : 0).toFixed(2))
    const drawChangedRateValue = Number((drawOldRate !== 0 ? initialValues.drawRate - drawOldRate : 0).toFixed(2))
    const awayChangedRateValue = Number((awayOldRate !== 0 ? initialValues.awayRate - awayOldRate : 0).toFixed(2))

    if (homeChangedRateValue !== 0) {
      setHomeRateChangeClass(homeChangedRateValue > 0 ? 'up' : 'down')
      setHomeChangedRate(homeChangedRateValue)
      setRefHomeRate(initialValues.homeRate)
      setHomeOldRate(initialValues.homeRate)
      setHomeValue(initialValues.homeValue)
      addViewController()

      if (timerIDHome != null) {
        clearTimeout(timerIDHome)
      }

      const timerID = setTimeout(() => {
        setHomeRateChangeClass('')
        setHomeChangedRate(0)
        setHomeValue(initialValues.homeValue)
      }, OddsChangeAnimationStopTime)
      setTimerIDHome(timerID)
    }
    if (drawChangedRateValue !== 0) {
      setDrawRateChangeClass(drawChangedRateValue > 0 ? 'up' : 'down')
      setDrawChangedRate(drawChangedRateValue)
      setRefDrawRate(initialValues.drawRate)
      setDrawOldRate(initialValues.drawRate)
      setDrawValue(initialValues.drawValue)
      addViewController()

      if (timerIDDraw != null) {
        clearTimeout(timerIDDraw)
      }

      const timerID = setTimeout(() => {
        setDrawRateChangeClass('')
        setDrawChangedRate(0)
        setDrawValue(initialValues.drawValue)
      }, OddsChangeAnimationStopTime)
      setTimerIDDraw(timerID)
    }
    if (awayChangedRateValue !== 0) {
      setAwayRateChangeClass(awayChangedRateValue > 0 ? 'up' : 'down')
      setAwayChangedRate(awayChangedRateValue)
      setRefAwayRate(initialValues.awayRate)
      setAwayOldRate(initialValues.awayRate)
      setAwayValue(initialValues.awayValue)
      addViewController()

      if (timerIDAway != null) {
        clearTimeout(timerIDAway)
      }

      const timerID = setTimeout(() => {
        setAwayRateChangeClass('')
        setAwayChangedRate(0)
        setAwayValue(initialValues.awayValue)
      }, OddsChangeAnimationStopTime)
      setTimerIDAway(timerID)
    }
  }

  useOnMount(() => {
    addViewController()
  })

  useEffect(() => {
    checkOddsStatusChange()
  })

  const { homeTeamName, awayTeamName } = match

  const makeHeader = () => {
    return (
      <div className="odds-block-radio-btn--team center">
        {oddsBlockType === BLOCK_TYPE.HANDICAP ? (
          <span className="odds-block-radio-btn--team--type center">H</span>
        ) : (
          <span className="odds-block-radio-btn--team--type center">U/O</span>
        )}
        <span className={`${oddsBlockType} odds-block-radio-btn--team--num`}>
          <span className="rate">{specialBetValue}</span>
        </span>
        {markets.length < 2 && <span className="blank"/>}
        {markets.length > 1 && <CircleDownIco/>}
      </div>
    )
  }

  return (
    <Radio.Group
      key={groupKey}
      value={field}
      disabled={disabled}
      className={
        oddsBlockType === BLOCK_TYPE.LIVE && isTwoWay ? 'odds-block-radio-group two-way' : 'odds-block-radio-group'
      }
    >
      <Radio.Button
        value={homeValue}
        onClick={e => {
          onClickField(e)
        }}
        className={`odds-block-radio-btn ${homeRateChangeClass}`}
        disabled={disabledHome}
        css={isHomeHit ? Hit : ''}
      >
        {!disabledHome && homeChangedRate !== 0 && (
          <ChangeNum className="left rate-change">
            <span className={`${homeRateChangeClass}-num`}>{Math.abs(homeChangedRate)}</span>
            {homeChangedRate > 0 ? PointUp : PointDown}
          </ChangeNum>
        )}
        <div className="odds-block-radio-btn--team">
          {oddsBlockType === BLOCK_TYPE.UNDEROVER ? (
            <span className="odds-block-radio-btn--team--type">언더</span>
          ) : (
            <span className="odds-block-radio-btn--team--type">홈 승</span>
          )}
          <span className="odds-block-radio-btn--team--num">
            <span className="rate">{convertRate(authStore.isSignedIn, homeRate, producerId === ProducerType.LIVEODDS && homeRate < minimumLiveMatchOutcomeRate)}</span>
            {oddsBlockType === BLOCK_TYPE.UNDEROVER && Down}
          </span>
        </div>
      </Radio.Button>

      {(oddsBlockType === BLOCK_TYPE.WDL || oddsBlockType === BLOCK_TYPE.LIVE) && !isTwoWay && (
        <Radio.Button
          key={`${groupKey}:draw`}
          className={`odds-block-radio-btn center ${drawRateChangeClass}`}
          value={drawValue}
          onClick={e => {
            onClickField(e)
          }}
          disabled={disabledDraw}
          css={isDrawHit ? Hit : ''}
        >
          {!disabledDraw && drawChangedRate !== 0 && (
            <ChangeNum className="left rate-change">
              <span className={`${drawRateChangeClass}-num`}>{Math.abs(drawChangedRate)}</span>
              {drawChangedRate > 0 ? PointUp : PointDown}
            </ChangeNum>
          )}
          <div className="odds-block-radio-btn--team center">
            <span className="odds-block-radio-btn--team--type">무</span>
            <span className="odds-block-radio-btn--team--num">
              {/* eslint-disable-next-line no-nested-ternary */}
              <span
                className="rate">{pageType === PAGE_TYPE.MATCHUP_RESULT ? '무' : convertRate(authStore.isSignedIn, drawRate, producerId === ProducerType.LIVEODDS && drawRate < minimumLiveMatchOutcomeRate)}</span>
            </span>
            <span className="blank"/>
          </div>
        </Radio.Button>
      )}

      {/* ************ custom ************ */}
      {/* ************ antd mobile accordion *************/}
      {(oddsBlockType === BLOCK_TYPE.HANDICAP || oddsBlockType === BLOCK_TYPE.UNDEROVER) && (
        <Accordion className={_.isEmpty(oddsBlockModalMarkets) ? 'match-btn-collapse no-match' : 'match-btn-collapse'}>
          <Accordion.Panel header={makeHeader()}>
            <OddsBlockModal
              homeTeamName={homeTeamName}
              awayTeamName={awayTeamName}
              className={isMobile ? 'odds-block-modal' : 'odds-block-modal pc'}
              odds={odds}
              match={match}
              markets={oddsBlockModalMarkets}
              onClickField={onClickField}
              pageType={pageType}
              homeKey={homeOutcomeType}
              awayKey={awayOutcomeType}
              oddsBlockType={oddsBlockType}
              disabled={disabled}
              field={field}
              enabledHomeFromViewController={enabledHomeFromViewController}
              enabledAwayFromViewController={enabledAwayFromViewController}
              homeOutcomeMetaId={homeKey}
              awayOutcomeMetaId={awayKey}
              minimumLiveMatchOutcomeRate={props.minimumLiveMatchOutcomeRate}
            />
          </Accordion.Panel>
        </Accordion>
      )}

      <Radio.Button
        key={`${groupKey}:away`}
        value={awayValue}
        onClick={e => {
          onClickField(e)
        }}
        disabled={disabledAway}
        className={`odds-block-radio-btn ${awayRateChangeClass}`}
        css={isAwayHit ? Hit : ''}
      >
        {!disabledAway && awayChangedRate !== 0 && (
          <ChangeNum className="right rate-change">
            <span className={`${awayRateChangeClass}-num`}>{Math.abs(awayChangedRate)}</span>
            {awayChangedRate > 0 ? PointUp : PointDown}
          </ChangeNum>
        )}
        <div className="odds-block-radio-btn--team">
          <span className="odds-block-radio-btn--team--num">
            <span
              className="rate">{convertRate(authStore.isSignedIn, awayRate, producerId === ProducerType.LIVEODDS && awayRate < minimumLiveMatchOutcomeRate)}</span>
            {oddsBlockType === BLOCK_TYPE.UNDEROVER && Up}
          </span>
          {oddsBlockType === BLOCK_TYPE.UNDEROVER ? (
            <span className="odds-block-radio-btn--team--type">오버</span>
          ) : (
            <span className="odds-block-radio-btn--team--type">원정 승</span>
          )}
        </div>
      </Radio.Button>
    </Radio.Group>
  )
}

export default observer(OddsButtonGroup)
